import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_circular-economy-hub-2/artifacts/7o4srgqk_BioResources%20%26%20Circular%20Economy%20Summit%20%26%20Expo%202026%20Brochure_compressed.pdf";
const DISMISS_KEY = "bioce_exit_intent_dismissed_session";

/**
 * Exit-intent modal: fires ONCE PER SESSION on desktop when user moves mouse to top of viewport.
 * Uses sessionStorage so dismissal lasts only for the current browser tab/session.
 * A returning visitor in a new session will see it again, but never twice within the same session.
 * Hides on /admin/*, /thank-you/, /register, and after first dismissal in the current session.
 */
export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/thank-you") || path === "/register") return;
    // Clean up legacy localStorage key from previous versions so users aren't permanently silenced
    if (typeof window !== "undefined") {
      try { localStorage.removeItem("bioce_exit_intent_dismissed_v1"); } catch (e) { /* noop */ }
    }
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    if (window.innerWidth < 1024) return; // desktop only

    // Arm after 8 sec on page (avoid bounce traffic immediately)
    const armTimer = setTimeout(() => setArmed(true), 8000);

    return () => clearTimeout(armTimer);
  }, []);

  useEffect(() => {
    if (!armed) return;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        if (sessionStorage.getItem(DISMISS_KEY)) return;
        setOpen(true);
        sessionStorage.setItem(DISMISS_KEY, "1");
        // Fire Lead event for retargeting audience
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("track", "ViewContent", { content_name: "Exit Intent Modal Shown" });
        }
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [armed]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="exit-intent-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[20px] max-w-md w-full overflow-hidden shadow-2xl relative"
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/90 hover:bg-white border border-gray-200 transition-colors"
              data-testid="exit-intent-close-btn"
              aria-label="Close"
            >
              <X size={16} className="text-forest-core" />
            </button>

            {/* Top yellow band */}
            <div className="bg-lemon-beam px-8 py-5 border-b-2 border-forest-core/10">
              <p className="text-forest-core/70 text-[10px] font-bold uppercase tracking-[0.22em]">Wait — before you go</p>
              <p className="text-forest-core text-xl font-extrabold tracking-tight mt-1">Don't miss out on the agenda</p>
            </div>

            <div className="px-8 py-7">
              <p className="text-muted-text text-sm leading-relaxed mb-6">
                Download the full brochure — 6 programme pillars, 13 conference tracks, confirmed speakers, exhibitor packages, and the BioLead Awards categories.
              </p>

              <a
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (typeof window !== "undefined" && window.fbq) {
                    window.fbq("track", "Lead", { content_name: "Brochure Download via Exit Intent" });
                  }
                }}
                data-testid="exit-intent-brochure-btn"
                className="w-full bg-forest-core text-lemon-beam font-bold text-sm py-3.5 rounded-[12px] hover:bg-deep-teal transition-colors inline-flex items-center justify-center gap-2 mb-3"
              >
                <Download size={15} /> Download Brochure (PDF)
              </a>

              <Link
                to="/register"
                onClick={close}
                data-testid="exit-intent-register-btn"
                className="w-full border-2 border-forest-core text-forest-core font-bold text-sm py-3 rounded-[12px] hover:bg-forest-core hover:text-lemon-beam transition-colors inline-flex items-center justify-center gap-2"
              >
                Claim Free Pass Instead <ArrowRight size={14} />
              </Link>

              <p className="text-center text-[11px] text-muted-text mt-5">
                500 seats only · 28 September 2026 · Mumbai
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
